(() => {
  let adding = false;
  const initialized = new WeakSet();

  function initialize(form) {
    if (initialized.has(form)) return;
    const products = form.querySelector('[data-products]');
    const shipping = form.querySelector('[data-drp-shipping]');
    const suggestions = form.querySelector('[data-drp-suggestions]');
    if (!products || !shipping || !suggestions) return;
    initialized.add(form);
    const cards = suggestions.querySelector('[data-drp-cards]');
    const navigation = suggestions.querySelector('[data-drp-navigation]');
    const position = suggestions.querySelector('[data-drp-position]');
    const status = suggestions.querySelector('[data-drp-status]');
    let index = 0;
    let revision = 0;
    let controller;
    let lastState;
    let signature = '';

    function showCard(nextIndex = 0) {
      const items = [...cards.querySelectorAll('[data-drp-card]')];
      index = items.length ? (nextIndex + items.length) % items.length : 0;
      cards.querySelectorAll('[data-drp-size-picker][open]').forEach(picker => { picker.open = false; });
      items.forEach((card, i) => { card.hidden = i !== index; });
      suggestions.hidden = !items.length;
      navigation.hidden = items.length < 2;
      position.textContent = items.length ? `${index + 1} / ${items.length}` : '';
    }

    async function supplement(ids, excluded, currentRevision) {
      const seen = new Set([...cards.querySelectorAll('[data-drp-card]')].map(card => card.dataset.productId));
      for (const id of ids.slice(0, 3)) {
        if (seen.size >= 3 || currentRevision !== revision) return;
        const url = new URL(suggestions.dataset.recommendationsUrl, window.location.origin);
        url.searchParams.set('section_id', 'drapeur-cart-recommendations');
        url.searchParams.set('product_id', id);
        url.searchParams.set('intent', 'complementary');
        url.searchParams.set('limit', '10');
        try {
          const response = await fetch(url, { signal: controller.signal });
          if (!response.ok) continue;
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (currentRevision !== revision) return;
          for (const card of html.querySelectorAll('[data-drp-recommendation-results] [data-drp-card]')) {
            const productId = card.dataset.productId;
            if (seen.has(productId) || excluded.has(productId)) continue;
            seen.add(productId);
            cards.append(card);
            if (seen.size >= 3) break;
          }
          showCard(index);
        } catch (error) {
          if (error.name === 'AbortError') return;
        }
      }
    }

    function synchronize() {
      const state = products.querySelector('[data-drp-cart-state]');
      if (!state || state === lastState) return;
      lastState = state;
      const shippingContent = state.content.querySelector('[data-drp-shipping-content]');
      shipping.replaceChildren(...[...shippingContent.childNodes].map(node => node.cloneNode(true)));
      const initialCards = state.content.querySelector('[data-drp-candidates]');
      const nextSignature = `${state.dataset.excluded}:${initialCards.innerHTML}`;
      if (signature === nextSignature) return;
      signature = nextSignature;
      revision += 1;
      if (controller) controller.abort();
      controller = new AbortController();
      const restoreFocus = cards.contains(document.activeElement);
      cards.replaceChildren(...[...initialCards.childNodes].map(node => node.cloneNode(true)));
      showCard();
      if (restoreFocus) {
        const target = cards.querySelector('a:not([tabindex="-1"])') || form.querySelector('.cart__checkout');
        target?.focus({ preventScroll: true });
      }
      if (Number(state.dataset.count) > 0) {
        const ids = [...new Set(state.dataset.sourceIds.split(',').filter(Boolean))];
        const excluded = new Set(state.dataset.excluded.split('|').filter(Boolean));
        supplement(ids, excluded, revision);
      }
    }

    suggestions.addEventListener('keydown', event => {
      if (event.key !== 'Escape') return;
      const picker = event.target.closest('[data-drp-size-picker][open]');
      if (!picker) return;
      event.preventDefault();
      event.stopPropagation();
      picker.open = false;
      picker.querySelector('summary').focus();
    });

    document.addEventListener('click', event => {
      suggestions.querySelectorAll('[data-drp-size-picker][open]').forEach(picker => {
        if (!picker.contains(event.target)) picker.open = false;
      });
    });

    suggestions.addEventListener('click', async event => {
      if (event.target.closest('[data-drp-previous]')) return showCard(index - 1);
      if (event.target.closest('[data-drp-next]')) return showCard(index + 1);
      const sizeOption = event.target.closest('[data-drp-size-option]');
      if (sizeOption && !sizeOption.disabled) {
        const picker = sizeOption.closest('[data-drp-size-picker]');
        const card = sizeOption.closest('[data-drp-card]');
        const add = card.querySelector('[data-drp-add]');
        picker.querySelectorAll('[data-drp-size-option]').forEach(option => {
          option.setAttribute('aria-pressed', String(option === sizeOption));
        });
        picker.querySelector('[data-drp-size-label]').textContent = sizeOption.dataset.variantTitle;
        card.querySelector('[data-drp-price]').textContent = sizeOption.dataset.price;
        add.dataset.variantId = sizeOption.dataset.variantId;
        add.disabled = false;
        picker.open = false;
        picker.querySelector('summary').focus();
        return;
      }
      const button = event.target.closest('[data-drp-add]');
      if (!button || button.disabled || adding) return;
      const card = button.closest('[data-drp-card]');
      const id = button.dataset.variantId;
      if (!id) return;
      adding = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.hidden = true;
      let succeeded = false;
      try {
        const response = await fetch(suggestions.dataset.addUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || suggestions.dataset.errorLabel);
        succeeded = true;
        button.textContent = suggestions.dataset.addedLabel;
        status.textContent = suggestions.dataset.addedLabel;
        status.hidden = false;
        document.dispatchEvent(new CustomEvent('cart:build'));
      } catch (error) {
        status.textContent = error.message === 'Failed to fetch' ? suggestions.dataset.errorLabel : (error.message || suggestions.dataset.errorLabel);
        status.hidden = false;
      } finally {
        adding = false;
        button.removeAttribute('aria-busy');
        if (!succeeded) button.disabled = false;
      }
    });

    new MutationObserver(synchronize).observe(products, { childList: true, subtree: true });
    synchronize();
  }

  function boot() {
    document.querySelectorAll('#CartDrawerForm, #CartPageForm').forEach(initialize);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
